import React, { useCallback } from 'react';
import { CityCGType, PoiCGType, SearchResultCGType } from '@/types/graphql/root';
import SpinnerLoader1818 from '@svg/common/spinner_loader_18_18.svg';
import NoticePrimary3636 from '@svg/survey/where/notice_primary_36_36.svg';
import ImageWithFallback from '@/app/ui/common/image-with-fallback';
import { getPoiFallbackImageUrl, getPoiType } from '@/utils';
import Star1212 from '@svg/trip/star_12_12.svg';
import SearchResultHeartActive2422 from '@svg/trip/search_result_heart_active_24_22.svg';
import SearchResultHeartDefault2422 from '@svg/trip/search_result_heart_default_24_22.svg';
import CheckLineGray2424 from '@svg/trip/check_line_gray_24_24.svg';
import CheckLineBlue2424 from '@svg/trip/check_line_blue_24_24.svg';
import { BrowserHistory } from 'history';
import { getPoiDetailByPlaceId, togglePoiSaved } from '@/utils/actions';
import useToggleSavedQueryCache from '@/app/hooks/useToggleSavedQueryCache';
import { useSetAtom } from 'jotai';
import { blackTransparentLoaderModalAtom, centerToastModalAtom } from '@/atom/modal';
import Search1717 from '@svg/trip/search_17_17.svg';
import { ITINERARY_HEIGHT_MODE_CONDITIONS, ItineraryHeightModeType } from '@/app/ui/trip/client-trip-page';

interface TripSearchViewProps {
  searchValue: string;
  loadingSearchPois: boolean;
  hasSearched: boolean;
  searchResults: SearchResultCGType[];
  selectedSearchResult: SearchResultCGType | null;
  savedPois: PoiCGType[];
  tripId: string;
  onClickSearchPoi: (poi: SearchResultCGType) => void;
  onClickAddDestinations: () => void;
  addDestinationsPosition: {
    day: number;
    pos: number;
    replace?: boolean;
    replaceDestId?: string;
  } | null;
  isEditRole: boolean;
  itineraryHeightMode: ItineraryHeightModeType;
  setHistoryStack: React.Dispatch<
    React.SetStateAction<
      {
        name?: string;
        afterAction?: () => void;
      }[]
    >
  >;
  history: BrowserHistory | null;
  tripCities: CityCGType[] | null;
}

export default function TripSearchView({
  searchValue,
  loadingSearchPois,
  hasSearched,
  searchResults,
  selectedSearchResult,
  savedPois,
  tripId,
  onClickSearchPoi,
  onClickAddDestinations,
  addDestinationsPosition,
  isEditRole,
  itineraryHeightMode,
  setHistoryStack,
  history,
  tripCities,
}: TripSearchViewProps) {
  const { toggleSaved } = useToggleSavedQueryCache();
  const setCenterToastModal = useSetAtom(centerToastModalAtom);
  const setBlackTransparentLoaderModal = useSetAtom(blackTransparentLoaderModalAtom);

  const onClickHeartByPlaceId = useCallback(async (placeId: string) => {
    const togglePoiSaveResponse = await togglePoiSaved(null, placeId);
    if (togglePoiSaveResponse.status === 200 && togglePoiSaveResponse.togglePoiSave) {
      toggleSaved(togglePoiSaveResponse.togglePoiSave);
    } else {
      console.error(togglePoiSaveResponse);
      setCenterToastModal({
        renderingTime: 1500,
        text: '로그인이\n필요 합니다.',
      });
    }
  }, []);

  const navigateToPoiDetail = useCallback(
    (poiId: string) => {
      history?.push(`/trip/${tripId}?poiId=${poiId}`);
    },
    [history, tripId],
  );

  const onClickPoi = useCallback(
    (searchResult: SearchResultCGType) => {
      const hasPoiId = searchResult.poi?.id;

      setHistoryStack((prev) => [...prev, {}]);

      if (hasPoiId) {
        navigateToPoiDetail(hasPoiId);
        return;
      }

      if (searchResult.placeId) {
        setBlackTransparentLoaderModal(true);
        getPoiDetailByPlaceId(searchResult.placeId)
          .then((res) => {
            if (res.data?.id) {
              navigateToPoiDetail(res.data.id);
            }
          })
          .finally(() => {
            setBlackTransparentLoaderModal(false);
          });
      }
    },
    [navigateToPoiDetail, setHistoryStack, setBlackTransparentLoaderModal],
  );

  return (
    <>
      {searchValue !== '' && loadingSearchPois && <SpinnerLoader1818 className='mx-auto mt-72pxr animate-spin' />}
      {searchValue !== '' && !loadingSearchPois && hasSearched && searchResults.length === 0 && (
        <div className='flex flex-col items-center pt-40pxr gap-16pxr px-51pxr'>
          <NoticePrimary3636 className='flex-none' />
          <div className='flex flex-col text-center gap-4pxr'>
            <p className='gap-6pxr sub-heading-16-m'>선택한 위치에 등록된 검색 결과가 없어요.</p>
            <p className='text-gray-700 text-14pxr'>지도를 이동하거나 검색 범위를 넓혀보세요.</p>
          </div>
        </div>
      )}
      {!loadingSearchPois && !hasSearched && searchResults.length === 0 && (
        <div className='flex flex-col items-center pt-40pxr gap-6pxr text-gray-700 justify-center text-14pxr'>
          <p>찾고 싶은 장소를 입력한 후</p>
          <p>검색 버튼을 눌러주세요.</p>
          {tripCities && tripCities.length > 1 && (
            <>
              <p className='text-new-point-500'>선택한 도시 외 인근 지역의 명소도</p>
              <p className='text-new-point-500'>함께 표시될 수 있어요.</p>
            </>
          )}
        </div>
      )}
      <div className='flex flex-col pb-120pxr'>
        {searchResults.map((searchResult, index) => {
          const rating = searchResult.poi?.ratings?.find((r) => r?.provider === 'google');
          const isSaved = (savedPois ?? []).find((v) => v.placeId === searchResult.placeId);
          return (
            <button
              key={'searchResult_' + index}
              className={`flex flex-row justify-between py-16pxr px-20pxr gap-5pxr border-b border-new-gray-200 ${
                selectedSearchResult?.placeId === searchResult.placeId ? 'bg-new-point-50' : ''
              }`}
              onClick={() => {
                onClickSearchPoi(searchResult);
              }}
            >
              <div className='flex flex-row gap-8pxr'>
                <div className='flex flex-row items-center gap-8pxr'>
                  {selectedSearchResult?.placeId === searchResult.placeId ? (
                    <CheckLineBlue2424 className='flex-none' />
                  ) : (
                    <CheckLineGray2424 className='flex-none' />
                  )}
                </div>
                <div className='relative flex-none'>
                  <button
                    key={'searchResultHeartImage_' + index}
                    className='w-50pxr h-50pxr'
                    onClick={(e) => {
                      e.stopPropagation();
                      if (searchResult.placeId) onClickHeartByPlaceId(searchResult.placeId);
                    }}
                  >
                    <ImageWithFallback
                      src={searchResult.poi?.images?.[0]?.imageUrl ?? ''}
                      alt={searchResult.name ?? 'poi'}
                      fallbackSrc={getPoiFallbackImageUrl(searchResult.poiType ?? '')}
                      width={50}
                      height={50}
                      className='flex-none object-cover w-50pxr h-50pxr rounded-4pxr'
                    />
                    <div className='absolute bottom-2 right-1'>
                      {isSaved ? (
                        <SearchResultHeartActive2422 className='flex-none' />
                      ) : (
                        <SearchResultHeartDefault2422 className='flex-none' />
                      )}
                    </div>
                  </button>
                </div>
                <div className='flex flex-col gap-4pxr'>
                  <p
                    className={`text-left text-gray-800 sub-heading-16-m line-clamp-2 ${
                      selectedSearchResult?.placeId === searchResult.placeId ? 'text-new-point-500' : ''
                    }`}
                  >
                    {searchResult.name}
                  </p>
                  <span className='flex flex-row items-start gap-5pxr'>
                    <span className='text-gray-700 body-12-r line-clamp-2 text-left'>
                      {getPoiType({
                        categories: searchResult.poi?.categories,
                        poiType: searchResult.poiType,
                      })}
                    </span>
                    {rating && (
                      <div className='flex items-center flex-none h-18pxr felx gap-2pxr'>
                        <Star1212 className='flex-none' />
                        <p className='text-gray-700 body-12-r'>{rating.rating}</p>
                      </div>
                    )}
                  </span>
                </div>
              </div>
              <div className='flex flex-col justify-center items-center w-42pxr shrink-0'>
                <button
                  className={`text-white body-14-r rounded-full w-40pxr h-40pxr flex items-center justify-center ${
                    selectedSearchResult?.placeId === searchResult.placeId ? 'bg-new-point-100' : 'bg-new-point-50'
                  } `}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickPoi(searchResult);
                  }}
                >
                  <Search1717 />
                </button>
              </div>
            </button>
          );
        })}
      </div>

      {isEditRole &&
        !ITINERARY_HEIGHT_MODE_CONDITIONS.IS_MINIMALIZE(itineraryHeightMode) &&
        addDestinationsPosition?.replace && (
          <div
            className='fixed bottom-0 w-full pt-16pxr px-20pxr pb-34pxr max-w-[30rem] z-[1] rounded-t-16pxr bg-white'
            style={{
              boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.08)',
            }}
          >
            <button
              className={`w-full px-16pxr py-14pxr h-50pxr rounded-8pxr button-16-m ${
                selectedSearchResult ? 'bg-custom-black text-white' : 'bg-gray-300 text-white'
              }`}
              disabled={!selectedSearchResult}
              onClick={() => {
                onClickAddDestinations();
              }}
            >
              교체하기
            </button>
          </div>
        )}
    </>
  );
}
